/**
 * AppICEMAP — Toast
 * Imperative singleton notification system.
 *
 * Usage:
 *   Toast.show('Reporte publicado', 'success')
 *   Toast.show('Error al conectar', 'error', 5000)
 *   Toast.dismiss()
 */

/** @type {HTMLElement | null} */
let toastEl = null;

/** @type {number | null} */
let timerId = null;

/**
 * Show a toast notification.
 * @param {string} message
 * @param {'success' | 'error' | 'info' | 'warning'} type
 * @param {number} [duration=2500] Auto-dismiss in ms
 */
export function show(message, type = "info", duration = 2500) {
	// If already visible, dismiss and queue
	if (toastEl && toastEl.classList.contains("toast--visible")) {
		dismiss();
	}

	// Remove existing toast element
	if (toastEl) {
		toastEl.remove();
		toastEl = null;
	}

	const icons = {
		success: "check-circle",
		error: "x-circle",
		info: "info",
		warning: "warning",
	};

	const iconName = icons[type] || icons.info;

	toastEl = document.createElement("output");
	toastEl.className = `toast toast--${type}`;
	toastEl.setAttribute("role", "status");
	toastEl.setAttribute("aria-live", "polite");

	toastEl.innerHTML = `
    <i class="ph ph-${iconName} toast__icon" aria-hidden="true"></i>
    <span class="toast__message">${escapeHtml(message)}</span>
    <button class="toast__dismiss" aria-label="Cerrar notificación">
      <i class="ph ph-x" aria-hidden="true"></i>
    </button>
  `;

	// Dismiss button
	const dismissBtn = toastEl.querySelector(".toast__dismiss");
	if (dismissBtn) {
		dismissBtn.addEventListener("click", dismiss);
	}

	document.body.appendChild(toastEl);

	// Trigger enter animation
	requestAnimationFrame(() => {
		if (toastEl) toastEl.classList.add("toast--visible");
	});

	// Auto-dismiss
	if (timerId) clearTimeout(timerId);
	timerId = setTimeout(dismiss, duration);
}

/**
 * Dismiss the currently visible toast.
 */
export function dismiss() {
	if (timerId) {
		clearTimeout(timerId);
		timerId = null;
	}

	if (toastEl) {
		toastEl.classList.remove("toast--visible");
		toastEl.classList.add("toast--exiting");

		const el = toastEl;
		setTimeout(() => {
			el.remove();
			if (toastEl === el) toastEl = null;
		}, 200);
	}
}

function escapeHtml(str) {
	const div = document.createElement("div");
	div.textContent = str;
	return div.innerHTML;
}

export default { show, dismiss };
